import React, { useState } from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { Alert, Badge, Button, Card, Col, Form, InputGroup } from 'react-bootstrap';
import Loading from './components/Loading';
import useAdminData, { responseError } from './useAdminData';
import { adminFetch, AdminAuthError } from './adminAuth';
import { formatDate, formatMoney } from './format';

// Same limits the API enforces (column sizes in consignment.sql)
const FIELDS = [
  { name: 'firstName', label: 'First name', max: 64, width: 6 },
  { name: 'lastName', label: 'Last name', max: 64, width: 6 },
  { name: 'emailAddress', label: 'Email', max: 255, width: 6, type: 'email' },
  { name: 'phoneNumber', label: 'Phone', max: 16, width: 6 },
  { name: 'streetAddress', label: 'Street address', max: 128, width: 12 },
  { name: 'city', label: 'City', max: 255, width: 6 },
  { name: 'state', label: 'State', max: 2, width: 2 },
  { name: 'zipcode', label: 'Zipcode', max: 10, width: 4 }
];

const validationSchema = Yup.object().shape(
  FIELDS.reduce((shape, field) => {
    let rule = Yup.string().trim().required('Required').max(field.max, `${field.max} characters or fewer`);
    if (field.type === 'email') {
      rule = rule.email('Not a valid email address');
    }
    return { ...shape, [field.name]: rule };
  }, {})
);

export function resumeLink(consignmentHash, origin = window.location.origin) {
  return consignmentHash ? `${origin}/resumeConsignment?consignmentId=${consignmentHash}` : null;
}

/** Only the fields that differ from the saved consigner, trimmed */
export function changedFields(values, saved) {
  return FIELDS.reduce((changes, { name }) => {
    const value = (values[name] || '').trim();
    return value !== (saved[name] || '') ? { ...changes, [name]: value } : changes;
  }, {});
}

function ResumeLink({ hash }) {
  const [copied, setCopied] = useState(false);
  const link = resumeLink(hash);
  if (!link) {
    return <p className="text-muted">No resume link for this consigner.</p>;
  }
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
    } catch (err) {
      setCopied(false);
    }
  };
  return (
    <InputGroup size="sm">
      <Form.Control readOnly value={link} onFocus={event => event.target.select()} aria-label="Resume link" />
      <InputGroup.Append>
        <Button variant="outline-secondary" onClick={copy}>{copied ? 'Copied' : 'Copy'}</Button>
      </InputGroup.Append>
    </InputGroup>
  );
}

export default function ConsignerDetail() {
  const { id } = useParams();
  const history = useHistory();
  const { data: consigner, error, loading, waking } = useAdminData(`/admin/consigners/${id}`);
  const [saved, setSaved] = useState(null);
  const [saveMessage, setSaveMessage] = useState(null);

  if (loading && !consigner) {
    return <Loading waking={waking} />;
  }
  if (error) {
    return (
      <div>
        <Alert variant="danger">{error}</Alert>
        <Link to="/admin/consigners">Back to consigners</Link>
      </div>
    );
  }

  const current = saved || consigner;
  const initialValues = FIELDS.reduce((values, { name }) => ({ ...values, [name]: current[name] || '' }), {});

  const save = async (values, { setSubmitting }) => {
    setSaveMessage(null);
    const changes = changedFields(values, current);
    if (Object.keys(changes).length === 0) {
      setSaveMessage({ variant: 'info', text: 'Nothing changed.' });
      setSubmitting(false);
      return;
    }
    try {
      const response = await adminFetch(`/admin/consigners/${id}`, { method: 'PATCH', body: changes });
      if (!response.ok) {
        throw new Error(await responseError(response));
      }
      const updated = await response.json();
      // The form reinitializes from the saved consigner
      setSaved(updated);
      setSaveMessage({ variant: 'success', text: 'Saved.' });
    } catch (err) {
      if (err instanceof AdminAuthError) {
        history.replace('/admin/login');
        return;
      }
      setSaveMessage({ variant: 'danger', text: err.message });
    }
    setSubmitting(false);
  };

  return (
    <div>
      <Link to="/admin/consigners" className="small">&larr; All consigners</Link>
      <div className="d-flex align-items-center mt-1 mb-3">
        <h2 className="mb-0">{current.firstName} {current.lastName}</h2>
        <span className="ml-3 text-muted">#{current.id}</span>
        {current.isVendor && <Badge variant="info" className="ml-2">Vendor {current.vendorId}</Badge>}
      </div>

      <Card className="mb-3">
        <Card.Body>
          <p className="mb-2">
            <Link to={`/admin/items?consigner=${current.id}`}>
              <strong>{current.itemCount}</strong> item{current.itemCount === 1 ? '' : 's'}
            </Link>,
            {' '}<strong>{formatMoney(current.itemValue)}</strong> total asking value
            {current.createdAt && <span className="text-muted"> · signed up {formatDate(current.createdAt)}</span>}
            {' · '}{current.emailContactAgreement ? 'agreed to sale emails' : 'no sale emails'}
          </p>
          <Form.Label className="small text-muted mb-1">Resume link (lets them sign back in and edit items)</Form.Label>
          <ResumeLink hash={current.consignmentHash} />
        </Card.Body>
      </Card>

      <Card>
        <Card.Header>Contact details</Card.Header>
        <Card.Body>
          {saveMessage && <Alert variant={saveMessage.variant}>{saveMessage.text}</Alert>}
          <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={save} enableReinitialize>
            {({ values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting, dirty }) => (
              <Form noValidate onSubmit={handleSubmit}>
                <Form.Row>
                  {FIELDS.map(field => (
                    <Form.Group as={Col} md={field.width} key={field.name} controlId={`consigner-${field.name}`}>
                      <Form.Label>{field.label}</Form.Label>
                      <Form.Control
                        name={field.name}
                        type={field.type || 'text'}
                        value={values[field.name]}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched[field.name] && !!errors[field.name]}
                      />
                      <Form.Control.Feedback type="invalid">{errors[field.name]}</Form.Control.Feedback>
                    </Form.Group>
                  ))}
                </Form.Row>
                {values.emailAddress.trim() !== (current.emailAddress || '') && (
                  <Alert variant="warning" className="small">
                    Changing the email also changes their resume link: the old link stops working. After
                    saving, send them the new link shown above.
                  </Alert>
                )}
                <Button type="submit" disabled={isSubmitting || !dirty}>
                  {isSubmitting ? 'Saving...' : 'Save changes'}
                </Button>
              </Form>
            )}
          </Formik>
        </Card.Body>
      </Card>
    </div>
  );
}
